import { useEffect, useState } from "react";
import { api, API_BASE } from "../lib/api";
import Layout from "../components/Layout";
import { toast } from "sonner";
import { Play, User, ShieldCheck, RefreshCw } from "lucide-react";
import { Link } from "react-router-dom";

const STATUSES = ["Submitted","Approved","Kept Internal","New Response Requested"];
const TONE = {
  "Submitted":"bg-sky-50 text-sky-700 border-sky-200",
  "Approved":"bg-emerald-50 text-emerald-700 border-emerald-200",
  "Kept Internal":"bg-slate-100 text-slate-600 border-slate-200",
  "New Response Requested":"bg-amber-50 text-amber-800 border-amber-300",
};

export default function MediaReview() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("Submitted");
  const [type, setType] = useState("");
  const [playing, setPlaying] = useState(null);

  const load = async () => {
    const p = {};
    if (status) p.approval_status = status;
    if (type) p.media_type = type;
    const { data } = await api.get("/media", { params: p });
    setRows(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, type]);

  const act = async (mid, action) => {
    try {
      let body = {};
      if (action==="approve") body = { approval_status:"Approved", employer_visible:true };
      else if (action==="internal") body = { approval_status:"Kept Internal", employer_visible:false };
      else if (action==="request") body = { approval_status:"New Response Requested" };
      await api.patch(`/media/${mid}`, body);
      toast.success("Updated");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <Layout>
      <div className="mb-6 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-widest text-sky-600 mb-1">Recruiter Media Review</div>
          <h1 className="font-display text-3xl font-bold text-slate-900 tracking-tight">Candidate media review queue</h1>
          <p className="text-sm text-slate-500 mt-1">Review candidate video introductions, interview responses and role auditions. Approve to make available to authorized employers.</p>
        </div>
        <button onClick={load} className="h-9 px-3 rounded-md border border-slate-300 text-sm inline-flex items-center gap-1.5" data-testid="media-refresh"><RefreshCw size={13}/> Refresh</button>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="p-4 border-b border-slate-200 bg-slate-50/50 flex flex-wrap gap-3 items-center">
          <select data-testid="filter-status" value={status} onChange={e=>setStatus(e.target.value)} className="h-9 px-3 rounded-md border border-slate-200 text-sm bg-white">
            <option value="">All statuses</option>
            {STATUSES.map(s=><option key={s}>{s}</option>)}
          </select>
          <select data-testid="filter-type" value={type} onChange={e=>setType(e.target.value)} className="h-9 px-3 rounded-md border border-slate-200 text-sm bg-white">
            <option value="">All media types</option>
            <option value="VIDEO_INTRODUCTION">Video Introduction</option>
            <option value="INTERVIEW_AUDIO_RESPONSE">Interview Response</option>
            <option value="SALES_PITCH">Sales Pitch</option>
            <option value="MOCK_CALL">Mock Call</option>
            <option value="ROLE_AUDITION_AUDIO">Role Audition (Audio)</option>
            <option value="ROLE_AUDITION_VIDEO">Role Audition (Video)</option>
            <option value="WORK_SAMPLE_MEDIA">Work Sample</option>
          </select>
          <div className="text-xs text-slate-500 ml-auto">{rows.length} items</div>
        </div>

        {rows.length===0 ? <div className="p-10 text-center text-sm text-slate-500">Nothing in the queue for this filter.</div> : (
          <div className="divide-y divide-slate-100">
            {rows.map(m => (
              <div key={m.id} className="p-4" data-testid={`review-row-${m.id}`}>
                <div className="flex items-start gap-4 flex-wrap">
                  <div className="w-10 h-10 rounded-full bg-sky-50 text-sky-700 flex items-center justify-center shrink-0"><User size={16}/></div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Link to={`/candidates/${m.candidate_id}`} className="font-semibold text-slate-900 hover:text-sky-700" data-testid={`goto-cand-${m.candidate_id}`}>{m.candidate_name}</Link>
                      <span className="text-[10px] px-1.5 py-0.5 rounded border border-slate-200 text-slate-500 font-mono">{m.media_type}</span>
                      <span className={`text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded border ${TONE[m.approval_status]||"bg-slate-50 text-slate-500 border-slate-200"}`}>{m.approval_status || "Submitted"}</span>
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">{m.candidate_role} · {m.candidate_country} · Submitted {new Date(m.submitted_at).toLocaleString()}{m.role_family?` · ${m.role_family}`:""}</div>
                    {m.title && <div className="text-sm text-slate-800 mt-1">{m.title}</div>}
                  </div>
                  <div className="flex gap-1.5 flex-wrap">
                    <button data-testid={`review-play-${m.id}`} onClick={()=>setPlaying(playing===m.id?null:m.id)} className="h-8 px-3 text-[11px] rounded border border-slate-300 inline-flex items-center gap-1 font-semibold"><Play size={11}/>{playing===m.id?"Hide":"Play"}</button>
                    {m.approval_status!=="Approved" && <button data-testid={`review-approve-${m.id}`} onClick={()=>act(m.id,"approve")} className="h-8 px-3 text-[11px] rounded border border-emerald-300 bg-emerald-50 text-emerald-800 font-semibold">Approve</button>}
                    {m.approval_status!=="Kept Internal" && <button onClick={()=>act(m.id,"internal")} className="h-8 px-3 text-[11px] rounded border border-slate-300 text-slate-700 font-semibold">Internal only</button>}
                    <button data-testid={`review-request-${m.id}`} onClick={()=>act(m.id,"request")} className="h-8 px-3 text-[11px] rounded border border-amber-300 bg-amber-50 text-amber-800 font-semibold">Request new</button>
                  </div>
                </div>
                {playing===m.id && (
                  <div className="mt-3">
                    {(m.media_type.includes("VIDEO") || m.media_type==="VIDEO_INTRODUCTION") ? (
                      <video controls preload="none" className="w-full max-w-lg rounded border border-slate-200" src={`${API_BASE}/media/${m.id}/stream`} crossOrigin="use-credentials" data-testid={`review-player-${m.id}`}/>
                    ) : (
                      <audio controls preload="none" className="w-full max-w-lg" src={`${API_BASE}/media/${m.id}/stream`} crossOrigin="use-credentials" data-testid={`review-player-${m.id}`}/>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-4 text-[11px] text-slate-500 flex items-center gap-1.5"><ShieldCheck size={11}/> Employer visibility requires explicit approval here. Media is streamed on demand and never preloaded elsewhere.</div>
    </Layout>
  );
}
